import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BookUser, ChevronDown, Hash, Search } from 'lucide-react';
import { useState } from 'react';
import type { SubscriberListRef } from '@/features/subscribers';
import { pluralise } from '@/shared/lib/format';
import { tint } from './styles';

type Kind = 'all' | SubscriberListRef['kind'];
type Props = { lists: SubscriberListRef[]; value: string; onChange: (id: string) => void; disabled?: boolean };

const KINDS: [Kind, string][] = [['all', 'All'], ['topic', 'Topic lists'], ['client_book', 'Client books']];
const GROUPS: [SubscriberListRef['kind'], string][] = [['topic', 'Topic lists'], ['client_book', 'Client books']];
const SHOWN = 30;

const kindName = (l: SubscriberListRef) => (l.kind === 'topic' ? 'Topic list' : 'Client book');
const detail = (l: SubscriberListRef) => (l.kind === 'topic' ? 'Shared topic list' : `Owned by ${l.ownerName ?? 'an RM'}`);
const ListIcon = ({ l, size }: { l: SubscriberListRef; size: number }) => (l.kind === 'topic' ? <Hash size={size} aria-hidden /> : <BookUser size={size} aria-hidden />);

function Option({ l, isOn, onPick }: { l: SubscriberListRef; isOn: boolean; onPick: () => void }) {
  return (
    <ButtonBase role="radio" aria-checked={isOn} onClick={onPick} sx={optionSx(isOn)}>
      <Box sx={radioSx(isOn)} aria-hidden />
      <Stack sx={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{l.name}</Typography>
        <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>{detail(l)}</Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>{pluralise(l.optedInCount, 'number')}</Typography>
    </ButtonBase>
  );
}

function Filters({ kind, onKind, q, onQ, total }: { kind: Kind; onKind: (k: Kind) => void; q: string; onQ: (q: string) => void; total: number }) {
  return (
    <Stack sx={{ gap: 1, p: 1.25, borderBottom: 1, borderColor: 'divider' }}>
      <TextField
        size="small"
        placeholder={`Search ${pluralise(total, 'list')}`}
        value={q}
        onChange={(e) => { onQ(e.target.value); }}
        slotProps={{ htmlInput: { 'aria-label': 'Search lists' }, input: { startAdornment: <InputAdornment position="start"><Search size={14} aria-hidden /></InputAdornment> } }}
      />
      <Stack direction="row" sx={{ gap: 0.5 }}>
        {KINDS.map(([k, label]) => (
          <ButtonBase key={k} aria-pressed={kind === k} onClick={() => { onKind(k); }} sx={chipSx(kind === k)}>{label}</ButtonBase>
        ))}
      </Stack>
    </Stack>
  );
}

/** Which list the message goes to: one list per message, searchable, topic lists and client books apart. */
export function ListPicker({ lists, value, onChange, disabled = false }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<Kind>('all');
  const current = lists.find((l) => l.id === value);
  const found = lists.filter((l) => (kind === 'all' || l.kind === kind) && l.name.toLowerCase().includes(q.trim().toLowerCase()));
  const shown = found.slice(0, SHOWN);
  const pick = (id: string) => { onChange(id); setAnchor(null); };

  return (
    <>
      <ButtonBase disabled={disabled} aria-haspopup="dialog" aria-label="Send to list" onClick={(e) => { setAnchor(e.currentTarget); }} sx={triggerSx(anchor !== null)}>
        {current ? <ListIcon l={current} size={14} /> : null}
        <Box component="span" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current?.name ?? 'Pick a list'}</Box>
        {current ? <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>· {kindName(current)}</Box> : null}
        <ChevronDown size={14} aria-hidden style={{ marginLeft: 'auto', flexShrink: 0 }} />
      </ButtonBase>
      <Popover open={anchor !== null} anchorEl={anchor} onClose={() => { setAnchor(null); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { width: 340, mt: 0.5, borderRadius: 1.5 } } }}>
        <Filters kind={kind} onKind={setKind} q={q} onQ={setQ} total={lists.length} />
        <Box role="radiogroup" aria-label="Lists" sx={{ maxHeight: 300, overflowY: 'auto', py: 0.5 }}>
          {GROUPS.map(([k, label]) => {
            const group = shown.filter((l) => l.kind === k);
            if (group.length === 0) return null;
            return (
              <Box key={k}>
                <Typography variant="caption" component="p" sx={{ px: 1.5, pt: 1, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
                {group.map((l) => <Option key={l.id} l={l} isOn={l.id === value} onPick={() => { pick(l.id); }} />)}
              </Box>
            );
          })}
          {shown.length === 0 ? <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>No list matches.</Typography> : null}
        </Box>
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary', px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
          Showing {shown.length} of {lists.length} · one list per message
        </Typography>
      </Popover>
    </>
  );
}

const triggerSx = (open: boolean) => ({
  gap: 0.75,
  justifyContent: 'flex-start',
  width: '100%',
  maxWidth: 300,
  height: 34,
  px: 1.25,
  borderRadius: 1,
  border: 1,
  borderColor: open ? 'primary.main' : 'divider',
  boxShadow: open ? `0 0 0 3px ${tint('primary-main', 12)}` : 'none',
  fontSize: '0.8125rem',
  transition: 'border-color 150ms, box-shadow 150ms',
  '&:hover': { borderColor: open ? 'primary.main' : 'surface.borderStrong' },
});

const chipSx = (isOn: boolean) => ({
  px: 1.25,
  height: 26,
  borderRadius: 5,
  border: 1,
  borderColor: isOn ? 'text.primary' : 'divider',
  bgcolor: isOn ? 'text.primary' : 'background.paper',
  color: isOn ? 'background.paper' : 'text.primary',
  fontSize: '0.75rem',
  fontWeight: 500,
});

const optionSx = (isOn: boolean) => ({
  width: '100%',
  gap: 1.25,
  px: 1.5,
  py: 1,
  bgcolor: isOn ? 'surface.accentSoft' : 'transparent',
  '&:hover': { bgcolor: isOn ? 'surface.accentSoft' : 'surface.sidebar' },
});

const radioSx = (isOn: boolean) => ({
  width: 16,
  height: 16,
  flexShrink: 0,
  borderRadius: '50%',
  border: isOn ? 5 : 1.5,
  borderColor: isOn ? 'primary.main' : 'surface.borderStrong',
  transition: 'border-width 150ms',
});
